<template>
  <div class="grid">
    <!-- Show Dropdown -->
    <div v-if="hasShowDropdown" class="flex flex-column gap-1 col-12 md:col-6">
      <label for="show_select">Show</label>
      <Dropdown
        id="show_select"
        :modelValue="campaign.show_id"
        @update:modelValue="
          $emit('update:campaign', { ...campaign, show_id: $event })
        "
        :options="shows"
        optionLabel="show_title"
        optionValue="show_id"
        placeholder="Podcast Show"
        checkmark
        filter
        :class="{ 'p-invalid': submitted && !campaign.show_id }"
      />
      <small v-if="submitted && !campaign.show_id" class="p-error">
        Show is required
      </small>
    </div>

    <div class="flex flex-column gap-1 col-12 md:col-3">
      <label for="start_date">Start Date</label>
      <Calendar
        id="start_date"
        :modelValue="campaign.start_date"
        @update:modelValue="
          $emit('update:campaign', { ...campaign, start_date: $event })
        "
        selectionMode="single"
        :manualInput="false"
      />
    </div>

    <div class="flex flex-column gap-1 col-12 md:col-3">
      <label for="end_date">End Date</label>
      <Calendar
        id="end_date"
        :modelValue="campaign.end_date"
        @update:modelValue="
          $emit('update:campaign', { ...campaign, end_date: $event })
        "
        selectionMode="single"
        :manualInput="false"
      />
    </div>

    <div class="flex flex-column gap-1 col-12 md:col-6">
      <label for="geo_wl">Geo Whitelist</label>
      <MultiSelect
        id="geo_wl"
        :modelValue="campaign.geos_wl"
        @update:modelValue="
          $emit('update:campaign', { ...campaign, geos_wl: $event })
        "
        :options="countries"
        optionValue="geo"
        optionLabel="name"
        placeholder="Select Countries"
        filter
        class="w-full"
      />
    </div>

    <div class="flex flex-column gap-1 col-12 md:col-6">
      <label for="geo_bl">Geo Blacklist</label>
      <MultiSelect
        id="geo_bl"
        :modelValue="campaign.geo_bl"
        @update:modelValue="
          $emit('update:campaign', { ...campaign, geo_bl: $event })
        "
        :options="countries"
        optionValue="geo"
        optionLabel="name"
        placeholder="Select Countries"
        filter
        class="w-full"
      />
    </div>

    <div class="flex flex-column gap-1 col-12 md:col-6">
      <label for="sellers_wl">Seller Whitelist</label>
      <MultiSelect
        id="sellers_wl"
        :modelValue="campaign.sellers_wl"
        @update:modelValue="
          $emit('update:campaign', { ...campaign, sellers_wl: $event })
        "
        :options="sellers"
        optionValue="value"
        :optionLabel="(option) => `[${option.id}] ${option.name}`"
        placeholder="Select Seller"
        filter
        class="w-full"
      />
    </div>

    <div class="flex flex-column gap-1 col-12 md:col-6">
      <label for="sellers_bl">Seller Blacklist</label>
      <MultiSelect
        id="sellers_bl"
        :modelValue="campaign.sellers_bl"
        @update:modelValue="
          $emit('update:campaign', { ...campaign, sellers_bl: $event })
        "
        :options="sellers"
        optionValue="value"
        :optionLabel="(option) => `[${option.id}] ${option.name}`"
        placeholder="Select Seller"
        filter
        class="w-full"
      />
    </div>

    <div class="col-12 md:col-3 flex flex-column gap-1">
      <label for="campaign_type">Campaign Type</label>
      <Dropdown
        id="campaign_type"
        :modelValue="campaign.campaign_type"
        @update:modelValue="
          $emit('update:campaign', { ...campaign, campaign_type: $event })
        "
        :options="campaignTypeOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Choose Campaign Type"
      />
      <div v-if="campaign.campaign_type === 'publisher'">
        <label for="publisher_id">Publisher ID</label>
        <Dropdown
          id="publisher_id"
          :modelValue="campaign.publisher_id"
          @update:modelValue="
            $emit('update:campaign', { ...campaign, publisher_id: $event })
          "
          :options="sellers"
          optionValue="value"
          :optionLabel="(option) => `[${option.id}] ${option.name}`"
          placeholder="Select Publisher"
          :class="{
            'p-invalid':
              submitted &&
              campaign.campaign_type === 'publisher' &&
              !campaign.publisher_id,
          }"
          filter
        />
        <small
          v-if="
            submitted &&
            campaign.campaign_type === 'publisher' &&
            !campaign.publisher_id
          "
          class="p-error"
        >
          Publisher ID is required
        </small>
      </div>
    </div>

    <div class="col-12 md:col-3 flex flex-column gap-1">
      <label for="status">Status</label>
      <Dropdown
        id="status"
        :modelValue="campaign.status"
        @update:modelValue="
          $emit('update:campaign', { ...campaign, status: $event })
        "
        :options="statusOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Choose Status"
      />
    </div>

    <div class="col-12 md:col-6">
      <div class="grid">
        <div class="col-3">
          <label for="growth_percentage">Growth (%)</label>
          <InputNumber
            id="growth_percentage"
            :modelValue="campaign.growth_percentage"
            @update:modelValue="
              $emit('update:campaign', {
                ...campaign,
                growth_percentage: $event,
              })
            "
            :min="0"
            :max="100"
            suffix="%"
            class="w-full"
            :inputStyle="{ width: '100%' }"
          />
        </div>
        <div class="col-6">
          <label for="ctr">CTR</label>
          <InputText
            id="ctr"
            :modelValue="campaign.ctr"
            @update:modelValue="
              $emit('update:campaign', { ...campaign, ctr: $event })
            "
            :class="{ 'p-invalid': submitted && !campaign.ctr }"
            :disabled="campaign.status === 'Mellow mode'"
          />
          <small
            v-if="
              submitted && !campaign.ctr && campaign.status !== 'Mellow mode'
            "
            class="p-error"
          >
            CTR is required
          </small>
        </div>
      </div>
    </div>

    <!-- Promotions Per Interval - Only show if configurable OR if not configurable and type is "same" -->
    <template v-if="isIntervalConfigurable">
      <div class="col-12">
        <div class="grid">
          <div class="col-12 md:col-3">
            <label for="interval_policy_type"
              >Promotions Per Interval Mode</label
            >
            <Dropdown
              id="interval_policy_type"
              :modelValue="localIntervalPolicyType"
              @update:modelValue="handleIntervalPolicyTypeChange"
              :options="intervalPolicyOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select Mode"
              class="w-full"
            />
          </div>

          <!-- Same for all shows mode -->
          <div
            v-if="localIntervalPolicyType === 'same'"
            class="col-12 md:col-6"
          >
            <label for="promotions_per_interval">Promotions Per Interval</label>
            <div class="p-inputgroup">
              <InputText
                id="promotions_per_interval"
                :modelValue="campaign.promotions_per_interval"
                @update:modelValue="
                  $emit('update:campaign', {
                    ...campaign,
                    promotions_per_interval: $event,
                  })
                "
                :class="{
                  'p-invalid': submitted && !campaign.promotions_per_interval,
                }"
                :disabled="campaign.status === 'Mellow mode'"
              />
              <Button
                v-if="showCalculateButton"
                type="button"
                icon="pi pi-calculator"
                @click="$emit('calculate-promotions')"
                :disabled="campaign.status === 'Mellow mode'"
              />
            </div>
            <small
              v-if="
                submitted &&
                !campaign.promotions_per_interval &&
                campaign.status !== 'Mellow mode'
              "
              class="p-error"
            >
              Promotions Per Interval is required
            </small>
          </div>

          <!-- Random range mode -->
          <div
            v-if="localIntervalPolicyType === 'random'"
            class="col-12 md:col-4"
          >
            <label for="interval_min">Min Value</label>
            <InputNumber
              id="interval_min"
              :modelValue="localIntervalMin"
              @update:modelValue="handleIntervalMinChange"
              :min="0"
              class="w-full"
              :inputStyle="{ width: '100%' }"
              :class="{
                'p-invalid': submitted && localIntervalMin === null,
              }"
            />
            <small
              v-if="submitted && localIntervalMin === null"
              class="p-error"
            >
              Min value is required
            </small>
          </div>

          <div
            v-if="localIntervalPolicyType === 'random'"
            class="col-12 md:col-4"
          >
            <label for="interval_max">Max Value</label>
            <InputNumber
              id="interval_max"
              :modelValue="localIntervalMax"
              @update:modelValue="handleIntervalMaxChange"
              :min="0"
              class="w-full"
              :inputStyle="{ width: '100%' }"
              :class="{
                'p-invalid': submitted && localIntervalMax === null,
              }"
            />
            <small
              v-if="submitted && localIntervalMax === null"
              class="p-error"
            >
              Max value is required
            </small>
          </div>
        </div>
      </div>
    </template>

    <!-- Regular Promotions Per Interval for non-configurable mode -->
    <template v-else>
      <div class="col-12 md:col-6">
        <label for="promotions_per_interval">Promotions Per Interval</label>
        <div class="p-inputgroup">
          <InputText
            id="promotions_per_interval"
            :modelValue="campaign.promotions_per_interval"
            @update:modelValue="
              $emit('update:campaign', {
                ...campaign,
                promotions_per_interval: $event,
              })
            "
            :class="{
              'p-invalid': submitted && !campaign.promotions_per_interval,
            }"
            :disabled="campaign.status === 'Mellow mode'"
          />
          <Button
            v-if="showCalculateButton"
            type="button"
            icon="pi pi-calculator"
            @click="$emit('calculate-promotions')"
            :disabled="campaign.status === 'Mellow mode'"
          />
        </div>
        <small
          v-if="
            submitted &&
            !campaign.promotions_per_interval &&
            campaign.status !== 'Mellow mode'
          "
          class="p-error"
        >
          Promotions Per Interval is required
        </small>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Button from "primevue/button";
import Dropdown from "primevue/dropdown";
import Calendar from "primevue/calendar";
import MultiSelect from "primevue/multiselect";

// eslint-disable-next-line no-undef
const props = defineProps({
  campaign: {
    type: Object,
    required: true,
  },
  countries: {
    type: Array,
    required: true,
  },
  sellers: {
    type: Array,
    required: true,
  },
  shows: {
    type: Array,
    default: () => [],
  },
  hasShowDropdown: {
    type: Boolean,
    default: false,
  },
  showCalculateButton: {
    type: Boolean,
    default: true,
  },
  submitted: {
    type: Boolean,
    default: false,
  },
  isIntervalConfigurable: {
    type: Boolean,
    default: false,
  },
});

// eslint-disable-next-line no-undef
const emit = defineEmits(["update:campaign", "calculate-promotions"]);

const campaignTypeOptions = [
  { label: "promotion", value: "promotion" },
  { label: "publisher", value: "publisher" },
  { label: "spotify", value: "spotify" },
];

const statusOptions = [
  { label: "active", value: "active" },
  { label: "pending", value: "pending" },
  { label: "in-active", value: "in-active" },
  { label: "Mellow mode", value: "Mellow mode" },
];

const intervalPolicyOptions = [
  { label: "Same for all shows", value: "same" },
  { label: "Random (range)", value: "random" },
  { label: "Based on Downloads", value: "avgDownloadsBased" },
];

// Local state for interval policy
const localIntervalPolicyType = ref("same");
const localIntervalMin = ref(null);
const localIntervalMax = ref(null);

// Initialize from campaign data if exists
watch(
  () => props.campaign.intervalPolicy,
  (newPolicy) => {
    if (newPolicy && props.isIntervalConfigurable) {
      localIntervalPolicyType.value = newPolicy.type || "same";
      if (newPolicy.type === "random") {
        localIntervalMin.value = newPolicy.min ?? null;
        localIntervalMax.value = newPolicy.max ?? null;
      }
    }
  },
  { immediate: true }
);

const handleIntervalPolicyTypeChange = (newType) => {
  localIntervalPolicyType.value = newType;

  let updatedCampaign = { ...props.campaign };

  if (newType === "same") {
    // For "same", we keep intervalPolicy but remove policy-specific values
    updatedCampaign.intervalPolicy = { type: "same" };
  } else if (newType === "random") {
    const min = localIntervalMin.value ?? 0.5;
    const max = localIntervalMax.value ?? 1;
    localIntervalMin.value = min;
    localIntervalMax.value = max;
    updatedCampaign.intervalPolicy = { type: "random", min, max };
  } else if (newType === "avgDownloadsBased") {
    updatedCampaign.intervalPolicy = { type: "avgDownloadsBased" };
  }

  emit("update:campaign", updatedCampaign);
};

const handleIntervalMinChange = (value) => {
  localIntervalMin.value = value;
  emit("update:campaign", {
    ...props.campaign,
    intervalPolicy: {
      type: "random",
      min: value,
      max: localIntervalMax.value,
    },
  });
};

const handleIntervalMaxChange = (value) => {
  localIntervalMax.value = value;
  emit("update:campaign", {
    ...props.campaign,
    intervalPolicy: {
      type: "random",
      min: localIntervalMin.value,
      max: value,
    },
  });
};
</script>

<style scoped>
.p-inputnumber,
.p-inputtext,
.p-inputgroup {
  width: 100%;
}

.p-inputgroup {
  display: flex;
}

.p-inputgroup .p-inputtext {
  flex: 1;
}

.p-inputgroup .p-button {
  flex: 0 0 auto;
}

label {
  display: block;
  margin-bottom: 0.5rem;
}

input {
  max-width: 100%;
}
</style>
